/**
 * Listing Chat Debug Utility
 *
 * Provides simulated chat conversations for testing and demonstration purposes.
 * These simulations populate the listing chat with realistic conversation flows
 * without requiring manual interaction.
 *
 * ## Usage
 *
 * ### Method 1: URL Parameter (Easiest)
 * Add `?debug=flowType` to the listing page URL:
 * - `/lease/abc123?debug=soft-warning` - Populates with soft warning flow
 * - `/lease/abc123?debug=qualified` - Populates with qualified prospect flow
 * - `/lease/abc123?debug=disqualified` - Populates with disqualified prospect flow
 *
 * The chat will automatically populate when the page loads.
 *
 * ### Method 2: Browser Console
 * Open browser console and run:
 * ```javascript
 * // List available flows
 * window.debugChat.listFlows()
 *
 * // Populate chat with a flow
 * window.debugChat.populate('listingId', 'soft-warning')
 *
 * // Clear debug simulation
 * window.debugChat.clear('listingId')
 * ```
 *
 * ### Method 3: Programmatic
 * ```javascript
 * import { populateChatWithSimulation } from './helpers/listingChatDebug';
 * populateChatWithSimulation('listingId', 'soft-warning');
 * ```
 *
 * ## Available Flows
 *
 * - **soft-warning**: Demonstrates soft warning flow (move-in date preference)
 * - **qualified**: Fully qualified prospect who meets all requirements
 * - **disqualified**: Prospect who doesn't meet requirements (e.g., has pets when not allowed)
 *
 * ## Notes
 *
 * - Debug messages are stored in localStorage using the same keys as real chat
 * - To clear debug simulation, use `window.debugChat.clear('listingId')` or reset chat in UI
 * - Debug mode works even if chat persistence is disabled (for testing)
 */

/**
 * Simulated conversation: Soft Warning Flow
 *
 * Demonstrates the soft warning flow where a prospect's move-in date
 * doesn't align with landlord preference, but they're not disqualified.
 *
 * Property: 3-bedroom, 2-bathroom apartment
 * Available Date: February 1st, 2026
 * Prospect: Sarah
 * Move-in Date: March 15th, 2026 (6 weeks out, exceeds 4-week preference)
 */
export const SOFT_WARNING_FLOW = [
  {
    id: Date.now() - 5000,
    type: "ai",
    content:
      "Hi! I'm Leo, your AI assistant for this rental. This 3-bed, 2-bath is available 2/1/2026. I'll ask a few quick questions to see if you're a fit and help you see if you qualify. When are you hoping to move in?",
    timestamp: new Date(Date.now() - 5000),
  },
  {
    id: Date.now() - 4000,
    type: "user",
    content: "My current lease isn't up until March 15th.",
    timestamp: new Date(Date.now() - 4000),
  },
  {
    id: Date.now() - 3000,
    type: "ai",
    content:
      "Thank you for your interest! We can go ahead and process your application, but just so you're aware, the landlord would prefer to have this unit rented within the next 4 weeks, so the timing may not align. Would you like to move forward?",
    timestamp: new Date(Date.now() - 3000),
  },
  {
    id: Date.now() - 2000,
    type: "user",
    content: "Yes, I really love the location, so I'd like to apply anyway.",
    timestamp: new Date(Date.now() - 2000),
  },
  {
    id: Date.now() - 1000,
    type: "ai",
    content: "Great! Do you have any pets?",
    timestamp: new Date(Date.now() - 1000),
  },
];

/**
 * Simulated conversation: Qualified Prospect Flow
 *
 * Demonstrates a fully qualified prospect who meets all requirements.
 */
export const QUALIFIED_PROSPECT_FLOW = [
  {
    id: Date.now() - 6000,
    type: "ai",
    content:
      "Hi! I'm Leo, your AI assistant for this rental. This 3-bed, 2-bath is available 2/1/2026. I'll ask a few quick questions to see if you're a fit and help you see if you qualify. When are you hoping to move in?",
    timestamp: new Date(Date.now() - 6000),
  },
  {
    id: Date.now() - 5000,
    type: "user",
    content:
      "I'm looking to move in around February 1st, which works perfectly!",
    timestamp: new Date(Date.now() - 5000),
  },
  {
    id: Date.now() - 4000,
    type: "ai",
    content: "Perfect timing! Do you have any pets?",
    timestamp: new Date(Date.now() - 4000),
  },
  {
    id: Date.now() - 3000,
    type: "user",
    content: "No pets.",
    timestamp: new Date(Date.now() - 3000),
  },
  {
    id: Date.now() - 2000,
    type: "ai",
    content: "Great! What's your approximate credit score?",
    timestamp: new Date(Date.now() - 2000),
  },
  {
    id: Date.now() - 1000,
    type: "user",
    content: "I have excellent credit, around 750.",
    timestamp: new Date(Date.now() - 1000),
  },
  {
    id: Date.now(),
    type: "ai",
    content: "Excellent! Last question - what's your monthly income?",
    timestamp: new Date(),
  },
];

/**
 * Simulated conversation: Disqualified Prospect Flow
 *
 * Demonstrates a prospect who doesn't meet requirements.
 */
export const DISQUALIFIED_PROSPECT_FLOW = [
  {
    id: Date.now() - 5000,
    type: "ai",
    content:
      "Hi! I'm Leo, your AI assistant for this rental. This 3-bed, 2-bath is available 2/1/2026. I'll ask a few quick questions to see if you're a fit and help you see if you qualify. When are you hoping to move in?",
    timestamp: new Date(Date.now() - 5000),
  },
  {
    id: Date.now() - 4000,
    type: "user",
    content: "I need to move in by February 1st.",
    timestamp: new Date(Date.now() - 4000),
  },
  {
    id: Date.now() - 3000,
    type: "ai",
    content: "Perfect timing! Do you have any pets?",
    timestamp: new Date(Date.now() - 3000),
  },
  {
    id: Date.now() - 2000,
    type: "user",
    content: "Yes, I have two dogs.",
    timestamp: new Date(Date.now() - 2000),
  },
  {
    id: Date.now() - 1000,
    type: "ai",
    content:
      "I'm sorry, but this property doesn't allow pets. I appreciate your interest, and I'd be happy to help you find a pet-friendly property if you'd like.",
    timestamp: new Date(Date.now() - 1000),
  },
];

/**
 * Available debug conversation flows
 */
export const DEBUG_FLOWS = {
  "soft-warning": SOFT_WARNING_FLOW,
  qualified: QUALIFIED_PROSPECT_FLOW,
  disqualified: DISQUALIFIED_PROSPECT_FLOW,
};

/**
 * Populate listing chat with a simulated conversation
 *
 * @param {string} listingId - The listing ID
 * @param {string} flowType - Type of flow ('soft-warning', 'qualified', 'disqualified')
 */
export const populateChatWithSimulation = (
  listingId,
  flowType = "soft-warning"
) => {
  const flow = DEBUG_FLOWS[flowType];
  if (!flow) {
    console.error(`[ListingChatDebug] Unknown flow type: ${flowType}`);
    return;
  }

  if (!listingId) {
    console.error("[ListingChatDebug] listingId is required");
    return;
  }

  try {
    const storageKey = `public-leo-chat-${listingId}`;
    const timestampKey = `public-leo-chat-timestamp-${listingId}`;

    // Store the simulated messages
    localStorage.setItem(storageKey, JSON.stringify(flow));
    localStorage.setItem(timestampKey, Date.now().toString());

    console.log(
      `[ListingChatDebug] Populated chat with ${flowType} flow (${flow.length} messages)`
    );
    console.log("[ListingChatDebug] Storage key:", storageKey);

    return true;
  } catch (error) {
    console.error("[ListingChatDebug] Failed to populate chat:", error);
    return false;
  }
};

/**
 * Clear debug simulation from chat
 *
 * @param {string} listingId - The listing ID
 */
export const clearChatSimulation = (listingId) => {
  if (!listingId) {
    console.error("[ListingChatDebug] listingId is required");
    return;
  }

  try {
    const storageKey = `public-leo-chat-${listingId}`;
    const timestampKey = `public-leo-chat-timestamp-${listingId}`;

    localStorage.removeItem(storageKey);
    localStorage.removeItem(timestampKey);

    console.log("[ListingChatDebug] Cleared chat simulation");
    return true;
  } catch (error) {
    console.error("[ListingChatDebug] Failed to clear chat:", error);
    return false;
  }
};

/**
 * Check if URL has debug parameter and auto-populate if present
 *
 * Usage: Add ?debug=soft-warning to URL to auto-populate chat
 */
export const checkDebugParam = (listingId) => {
  if (typeof window === "undefined") return;

  const urlParams = new URLSearchParams(window.location.search);
  const debugFlow = urlParams.get("debug");

  if (debugFlow && DEBUG_FLOWS[debugFlow] && listingId) {
    console.log(
      `[ListingChatDebug] Auto-populating with ${debugFlow} flow from URL parameter`
    );
    populateChatWithSimulation(listingId, debugFlow);
  }
};

/**
 * Expose debug functions to window for console access
 *
 * Usage in browser console:
 * - window.debugChat.populate('listingId', 'soft-warning')
 * - window.debugChat.clear('listingId')
 * - window.debugChat.flows // List available flows
 */
if (typeof window !== "undefined") {
  window.debugChat = {
    populate: populateChatWithSimulation,
    clear: clearChatSimulation,
    flows: Object.keys(DEBUG_FLOWS),
    listFlows: () => {
      console.log("Available debug flows:", Object.keys(DEBUG_FLOWS));
      console.log('Usage: window.debugChat.populate("listingId", "flowType")');
      console.log(
        'Example: window.debugChat.populate("abc123", "soft-warning")'
      );
    },
  };

  console.log(
    "[ListingChatDebug] Debug functions available at window.debugChat"
  );
  console.log(
    "[ListingChatDebug] Run window.debugChat.listFlows() to see available flows"
  );
}
